import React, {useCallback, useEffect, useMemo, useState} from "react"
import {
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    HeaderGroup,
    useReactTable,
} from "@tanstack/react-table"
import {Filter} from "../third-party/react-table"

import ScrollX from "components/ScrollX"
import MainCard from "components/MainCard"
import Paper from "@mui/material/Paper"
import {
    Button, MenuItem, Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material"
import {Add, DocumentDownload, House2, Printer} from "iconsax-react"
import {FormattedMessage} from "react-intl"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import {debounce} from "lodash"

interface ReactTableProps<T> {
    columns: ColumnDef<T, unknown>[]
    data: T[]
    striped?: boolean
    title?: string
    modalToggler: () => void
    modalBox: () => void
    openModalViews: () => void
    selectedRows?: string[]
    onFilterChange?: (state: ColumnFiltersState) => void
    manualFiltering?: boolean
    onDeselectAll: () => void
    onPrintMunicipalityReport?: (municipalityId: string) => void // New prop for handling municipality report printing
    onPrintSelectedDog?: (ids: string[]) => void // New prop for handling municipality report printing

}

export default function BasicTableForDog<T>({
                                                columns,
                                                data,
                                                striped = false,
                                                title = "",
                                                modalToggler,
                                                modalBox,
                                                onDeselectAll,
                                                openModalViews,
                                                selectedRows = [],
                                                onPrintMunicipalityReport,
                                                onPrintSelectedDog,
                                                onFilterChange,
                                                manualFiltering = false,
                                            }: ReactTableProps<T>) {

    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const [sexFilter, setSexFilter] = useState<string>('all')
    const [sterilizationFilter, setSterilizationFilter] = useState<string>('all')


    const getMunicipalityFilter = () => {
        return columnFilters.find(filter => filter.id === 'municipalityId')?.value as string
    }

    const table = useReactTable({
        data,
        columns,
        state: { columnFilters },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        onColumnFiltersChange: setColumnFilters,
        manualFiltering,
    })


    useEffect(() => {
        if (onFilterChange) {
            const updatedFilters = columnFilters.filter(
                filter => filter.id !== "sex" && filter.id !== "isSterilizied"
            );

            if (sexFilter !== "all") {
                updatedFilters.push({ id: "sex", value: sexFilter });
            }

            if (sterilizationFilter !== "all") {
                updatedFilters.push({ id: "isSterilizied", value: sterilizationFilter });
            }

            onFilterChange(updatedFilters);
        }
    }, [sexFilter, sterilizationFilter, columnFilters]);


    return (
        <MainCard content={false}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{padding: 3}}>
                <Typography variant="h6" fontWeight="bold">
                    {title}
                </Typography>

                <Stack direction="row" spacing={2}>
                    {selectedRows.length > 0 && (

                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="contained"
                                onClick={onDeselectAll}
                            >
                                <FormattedMessage id="Delete selection" defaultMessage="Delete selection"/>
                            </Button>
                            <Button variant="contained" startIcon={<House2/>} onClick={modalBox}>
                                <FormattedMessage id="Move the selected dog/dogs"
                                                  defaultMessage="Move the selected dog/dogs"/>
                            </Button>

                            <Button variant="contained" startIcon={<DocumentDownload/>}
                                    onClick={() => onPrintSelectedDog?.(selectedRows)}>
                                <FormattedMessage id="Save the selected dogs"
                                                  defaultMessage="Save the selected dogs"/>
                            </Button>
                        </Stack>
                    )}

                    {getMunicipalityFilter() && (
                        <Button
                            variant="contained"
                            startIcon={<DocumentDownload/>}
                            onClick={() => onPrintMunicipalityReport?.(getMunicipalityFilter())}
                        >
                            <FormattedMessage id="Save Municipality Report"
                                              defaultMessage="Save Municipality Report"/>
                        </Button>
                    )}

                    {/* aggiunta filtraggio custom gender e sterilization */}
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                        <InputLabel><FormattedMessage id="Sex" defaultMessage="Sex" /></InputLabel>
                        <Select
                            value={sexFilter}
                            onChange={(e) => setSexFilter(e.target.value)}>
                            <MenuItem value="all">
                                <FormattedMessage id="All" defaultMessage="All" />
                            </MenuItem>
                            <MenuItem value="m">
                                <FormattedMessage id="Male" defaultMessage="Male" />
                            </MenuItem>
                            <MenuItem value="f">
                                <FormattedMessage id="Female" defaultMessage="Female" />
                            </MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                        <InputLabel><FormattedMessage id="Sterilization" defaultMessage="Sterilization"/></InputLabel>
                        <Select
                            value={sterilizationFilter}
                            onChange={(e) => setSterilizationFilter(e.target.value)}
                        >
                            <MenuItem value="all">
                                <FormattedMessage id="All" defaultMessage="All" />
                            </MenuItem>
                            <MenuItem value="false">
                                <FormattedMessage id="Non Sterilized" defaultMessage="Non Sterilized" />
                            </MenuItem>
                            <MenuItem value="true">
                                <FormattedMessage id="Sterilized" defaultMessage="Sterilized" />
                            </MenuItem>
                        </Select>
                    </FormControl>

                    <Button variant="contained" startIcon={<Add/>} onClick={modalToggler}>
                        <FormattedMessage id="Add Element" defaultMessage="Add Element"/>
                    </Button>
                </Stack>
            </Stack>
            <ScrollX>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            {table.getHeaderGroups().map((headerGroup: HeaderGroup<T>) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableCell key={header.id}
                                                   sx={{...(header.column.columnDef.minSize && {minWidth: header.column.columnDef.minSize})}}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHead>
                        <TableHead>
                            {table.getHeaderGroups().map((headerGroup: HeaderGroup<T>) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableCell key={header.id}
                                                   sx={{...(header.column.columnDef.minSize && {minWidth: header.column.columnDef.minSize})}}>
                                            {header.column.getCanFilter() &&
                                                <Filter column={header.column} table={table}/>}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHead>
                        <TableBody>
                            {table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id}
                                          className={striped ? (row.index % 2 === 0 ? "striped" : "") : ""}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </ScrollX>
        </MainCard>
    )
}
